import React from 'react';
import { createPortal } from 'react-dom';
import {
   AlertTriangle,
   CheckCircle2,
   CircleAlert,
   ExternalLink,
   Gauge,
   ShieldAlert,
   X,
} from 'lucide-react';
import styled, { keyframes, css } from 'styled-components';
import { useEdges, useNodes, useReactFlow } from 'reactflow';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { theme } from '../../../styles/theme';
import {
   collectWorkflowIssues,
   openWorkflowConnectionsPanel,
   WORKFLOW_ISSUES_OPEN_EVENT,
   WORKFLOW_ISSUES_TOGGLE_EVENT,
   WorkflowIssue,
} from '../../../services/validateWorkflowIssues';

type IssuesTab = 'critical' | 'warnings';

const fadeIn = keyframes`
   from { opacity: 0; }
   to { opacity: 1; }
`;

const riseIn = keyframes`
   from { opacity: 0; transform: translate3d(0, 18px, 0); }
   to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

export function IssuesLog() {
   const project = useSelector((state: RootState) => state.project);
   const nodes = useNodes();
   const edges = useEdges();
   const { setNodes, getNodes, getNode, setCenter, getZoom } = useReactFlow();
   const [isOpen, setIsOpen] = React.useState(false);
   const [tab, setTab] = React.useState<IssuesTab>('critical');
   const listRef = React.useRef<HTMLDivElement>(null);
   const focusClearTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

   const issues = React.useMemo(
      () => collectWorkflowIssues(nodes, edges, project),
      [nodes, edges, project]
   );
   const errors = React.useMemo(
      () => issues.filter((i) => i.severity === 'error'),
      [issues]
   );
   const warnings = React.useMemo(
      () => issues.filter((i) => i.severity === 'warning'),
      [issues]
   );

   React.useEffect(() => {
      const open = () => setIsOpen(true);
      const toggle = () => setIsOpen((v) => !v);
      window.addEventListener(WORKFLOW_ISSUES_OPEN_EVENT, open);
      window.addEventListener(WORKFLOW_ISSUES_TOGGLE_EVENT, toggle);
      return () => {
         window.removeEventListener(WORKFLOW_ISSUES_OPEN_EVENT, open);
         window.removeEventListener(WORKFLOW_ISSUES_TOGGLE_EVENT, toggle);
      };
   }, []);

   React.useEffect(() => {
      if (!isOpen) return;
      setTab(errors.length ? 'critical' : warnings.length ? 'warnings' : 'critical');
      listRef.current?.scrollTo(0, 0);
      const onKey = (e: KeyboardEvent) => {
         if (e.key === 'Escape') setIsOpen(false);
      };
      window.addEventListener('keydown', onKey);
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
         window.removeEventListener('keydown', onKey);
         document.body.style.overflow = prevOverflow;
      };
   }, [isOpen, errors.length, warnings.length]);

   React.useEffect(() => {
      return () => {
         if (focusClearTimerRef.current) clearTimeout(focusClearTimerRef.current);
      };
   }, []);

   const handleFocusIssue = (issue: WorkflowIssue) => {
      if (focusClearTimerRef.current) clearTimeout(focusClearTimerRef.current);

      // Resolve the live node by id first, then by index+name as a fallback
      // (guards against stale/mismatched ids after imports or clones).
      const resolveTarget = (list: ReturnType<typeof getNodes>) => {
         const byId = list.find((n) => n.id === issue.nodeId);
         if (byId) return byId;
         return list.find(
            (n) =>
               String(n.data?.index || '') === String(issue.nodeIndex || '') &&
               String(n.data?.name || '') === String(issue.nodeName || '')
         );
      };

      const targetId = resolveTarget(getNodes())?.id;

      setNodes((current) =>
         current.map((node) => {
            const isTarget = Boolean(targetId) && node.id === targetId;
            return {
               ...node,
               selected: isTarget,
               data: {
                  ...(node.data || {}),
                  issueFocusHighlight: isTarget ? issue.severity : undefined,
               },
            };
         })
      );

      setIsOpen(false);

      setTimeout(() => {
         try {
            const live = (targetId && getNode(targetId)) || resolveTarget(getNodes());
            if (!live) return;

            const width = live.width ?? 160;
            const height = live.height ?? 90;
            const x = (live.positionAbsolute?.x ?? live.position.x) + width / 2;
            const y = (live.positionAbsolute?.y ?? live.position.y) + height / 2;
            const zoom = Math.min(1.35, Math.max(getZoom(), 1.05));
            setCenter(x, y, { zoom, duration: 420 });
         } catch {
            /* ignore */
         }
      }, 60);

      focusClearTimerRef.current = setTimeout(() => {
         setNodes((current) => {
            let changed = false;
            const next = current.map((node) => {
               if (!(node.data as any)?.issueFocusHighlight) return node;
               changed = true;
               return {
                  ...node,
                  data: {
                     ...node.data,
                     issueFocusHighlight: undefined,
                  },
               };
            });
            return changed ? next : current;
         });
         focusClearTimerRef.current = null;
      }, 3000);
   };

   const visible = tab === 'critical' ? errors : warnings;
   const headline = !issues.length
      ? 'Looks ready to run'
      : errors.length
        ? 'Fix critical issues before Run'
        : 'Workflow can run — review performance tips';

   if (!isOpen) return null;

   return createPortal(
      <Overlay onClick={() => setIsOpen(false)} role="presentation">
         <Panel
            role="dialog"
            aria-modal="true"
            aria-label="Workflow issues"
            onClick={(e) => e.stopPropagation()}
         >
            <PanelHead $tone={!issues.length ? 'ok' : errors.length ? 'critical' : 'warn'}>
               <div className="icon">
                  {!issues.length ? (
                     <CheckCircle2 size={20} />
                  ) : errors.length ? (
                     <ShieldAlert size={20} />
                  ) : (
                     <Gauge size={20} />
                  )}
               </div>
               <div className="copy">
                  <h3>{headline}</h3>
                  <p>
                     {!issues.length
                        ? 'No missing connections or required options detected.'
                        : `${errors.length} critical · ${warnings.length} performance warning${warnings.length === 1 ? '' : 's'}. Click an item to jump to the node.`}
                  </p>
               </div>
               <button
                  type="button"
                  className="close"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close"
               >
                  <X size={18} />
               </button>
            </PanelHead>

            <Tabs role="tablist" aria-label="Issue categories">
               <TabButton
                  type="button"
                  role="tab"
                  aria-selected={tab === 'critical'}
                  $active={tab === 'critical'}
                  $tone="critical"
                  onClick={() => setTab('critical')}
               >
                  <CircleAlert size={14} />
                  <span>Critical</span>
                  <em>{errors.length}</em>
               </TabButton>
               <TabButton
                  type="button"
                  role="tab"
                  aria-selected={tab === 'warnings'}
                  $active={tab === 'warnings'}
                  $tone="warnings"
                  onClick={() => setTab('warnings')}
               >
                  <AlertTriangle size={14} />
                  <span>Performance</span>
                  <em>{warnings.length}</em>
               </TabButton>
            </Tabs>

            <TabHint>
               {tab === 'critical'
                  ? 'These block a reliable run — missing connections, operations, or required fields.'
                  : 'The workflow can still run, but these may waste steps, cost, or latency.'}
            </TabHint>

            <PanelList ref={listRef}>
               {visible.length ? (
                  visible.map((issue) => (
                     <IssueRow
                        type="button"
                        key={issue.id}
                        className={issue.severity}
                        onClick={() => handleFocusIssue(issue)}
                        title="Jump to this node"
                     >
                        <span className="rail" aria-hidden="true" />
                        <span className="body">
                           <span className="top">
                              <span className="title">{issue.nodeName}</span>
                              <span className="badge">
                                 {issue.severity === 'error' ? 'Critical' : 'Performance'}
                              </span>
                           </span>
                           {issue.nodeIndex ? (
                              <span className="meta">#{issue.nodeIndex} · {issue.action || 'node'}</span>
                           ) : null}
                           {issue.message ? <span className="msg">{issue.message}</span> : null}
                           {issue.hint ? <span className="hint">{issue.hint}</span> : null}
                        </span>
                        <span className="go" aria-hidden="true">
                           <ExternalLink size={15} />
                        </span>
                     </IssueRow>
                  ))
               ) : (
                  <EmptyState>
                     {tab === 'critical' ? (
                        <>
                           <CheckCircle2 size={22} />
                           <p>No critical blockers. You can run this workflow.</p>
                           {warnings.length ? (
                              <button type="button" onClick={() => setTab('warnings')}>
                                 Review {warnings.length} performance tip{warnings.length === 1 ? '' : 's'}
                              </button>
                           ) : null}
                        </>
                     ) : (
                        <>
                           <CheckCircle2 size={22} />
                           <p>No performance warnings right now.</p>
                           {errors.length ? (
                              <button type="button" onClick={() => setTab('critical')}>
                                 See {errors.length} critical issue{errors.length === 1 ? '' : 's'}
                              </button>
                           ) : null}
                        </>
                     )}
                  </EmptyState>
               )}
            </PanelList>

            <PanelFoot>
               <button type="button" className="ghost" onClick={() => setIsOpen(false)}>
                  Close
               </button>
               <button
                  type="button"
                  className="primary"
                  onClick={() => {
                     setIsOpen(false);
                     openWorkflowConnectionsPanel();
                  }}
               >
                  Open Connections
               </button>
            </PanelFoot>
         </Panel>
      </Overlay>,
      document.body
   );
}

const Overlay = styled.div`
   position: fixed;
   inset: 0;
   z-index: 2147483000;
   display: flex;
   align-items: flex-start;
   justify-content: center;
   padding: max(72px, 8vh) 1rem 1.5rem;
   background: rgba(15, 12, 35, 0.38);
   backdrop-filter: blur(10px) saturate(1.15);
   -webkit-backdrop-filter: blur(10px) saturate(1.15);
   animation: ${fadeIn} 0.18s ease;
`;

const Panel = styled.div`
   width: min(560px, 100%);
   height: min(78vh, 680px);
   max-height: min(78vh, 680px);
   display: flex;
   flex-direction: column;
   background: rgba(251, 250, 254, 0.92);
   border-radius: 18px;
   border: 1px solid rgba(230, 226, 246, 0.9);
   box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.65) inset,
      0 24px 60px -20px rgba(29, 20, 70, 0.45);
   overflow: hidden;
   animation: ${riseIn} 0.22s ease;
`;

const PanelHead = styled.div<{ $tone: 'ok' | 'critical' | 'warn' }>`
   display: flex;
   align-items: flex-start;
   gap: 12px;
   padding: 16px 16px 14px;
   border-bottom: 1px solid rgba(230, 226, 246, 0.9);
   flex-shrink: 0;
   background: ${({ $tone }) =>
      $tone === 'ok'
         ? 'linear-gradient(180deg, rgba(236, 253, 245, 0.95), rgba(251, 250, 254, 0.4))'
         : $tone === 'critical'
           ? 'linear-gradient(180deg, rgba(254, 226, 226, 0.75), rgba(251, 250, 254, 0.35))'
           : 'linear-gradient(180deg, rgba(255, 237, 213, 0.85), rgba(251, 250, 254, 0.35))'};

   .icon {
      width: 36px;
      height: 36px;
      border-radius: 11px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: ${({ $tone }) =>
         $tone === 'ok' ? '#d1fae5' : $tone === 'critical' ? '#fecaca' : '#fed7aa'};
      color: ${({ $tone }) =>
         $tone === 'ok' ? '#047857' : $tone === 'critical' ? '#b91c1c' : '#c2410c'};
   }

   .copy {
      min-width: 0;
      flex: 1;

      h3 {
         margin: 0 0 3px;
         font-size: 15px;
         font-weight: 700;
         color: ${theme.colors.text};
         letter-spacing: -0.015em;
      }

      p {
         margin: 0;
         font-size: 12.5px;
         line-height: 1.45;
         color: ${theme.colors.textSecondary};
      }
   }

   .close {
      border: 0;
      background: transparent;
      color: ${theme.colors.textSecondary};
      cursor: pointer;
      padding: 4px;
      border-radius: 8px;
      line-height: 0;

      &:hover {
         background: rgba(23, 18, 38, 0.06);
         color: ${theme.colors.text};
      }
   }
`;

const Tabs = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 6px;
   padding: 12px 14px 0;
   flex-shrink: 0;
`;

const TabButton = styled.button<{ $active: boolean; $tone: 'critical' | 'warnings' }>`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 7px;
   min-height: 38px;
   border-radius: 11px;
   border: 1px solid transparent;
   cursor: pointer;
   font-size: 12.5px;
   font-weight: 700;
   transition:
      background 0.15s ease,
      border-color 0.15s ease,
      color 0.15s ease,
      box-shadow 0.15s ease;

   ${({ $active, $tone }) =>
      $active
         ? $tone === 'critical'
            ? css`
                 background: #fff;
                 border-color: rgba(185, 28, 28, 0.22);
                 color: #b91c1c;
                 box-shadow: 0 4px 14px -8px rgba(185, 28, 28, 0.35);
              `
            : css`
                 background: #fff;
                 border-color: rgba(194, 65, 12, 0.22);
                 color: #c2410c;
                 box-shadow: 0 4px 14px -8px rgba(194, 65, 12, 0.3);
              `
         : css`
              background: rgba(241, 239, 250, 0.7);
              color: ${theme.colors.textSecondary};

              &:hover {
                 background: #fff;
                 color: ${theme.colors.text};
              }
           `}

   em {
      min-width: 20px;
      height: 20px;
      padding: 0 6px;
      border-radius: 999px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-style: normal;
      font-size: 11px;
      font-weight: 800;
      background: ${({ $active, $tone }) =>
         $active
            ? $tone === 'critical'
               ? '#fee2e2'
               : '#ffedd5'
            : 'rgba(156, 151, 179, 0.18)'};
      color: inherit;
   }
`;

const TabHint = styled.p`
   margin: 8px 16px 0;
   font-size: 11.5px;
   line-height: 1.4;
   color: ${theme.colors.textTertiary};
   flex-shrink: 0;
`;

const PanelList = styled.div`
   flex: 1 1 0;
   min-height: 0;
   height: 0; /* force flex child to take remaining space so overflow scrolls */
   overflow-x: hidden;
   overflow-y: scroll;
   padding: 12px 14px;
   overscroll-behavior: contain;
   -webkit-overflow-scrolling: touch;

   scrollbar-width: thin;
   scrollbar-color: rgba(107, 101, 132, 0.45) rgba(241, 239, 250, 0.8);

   &::-webkit-scrollbar {
      width: 8px;
   }
   &::-webkit-scrollbar-track {
      background: rgba(241, 239, 250, 0.8);
      border-radius: 999px;
   }
   &::-webkit-scrollbar-thumb {
      background: rgba(107, 101, 132, 0.4);
      border-radius: 999px;
   }
   &::-webkit-scrollbar-thumb:hover {
      background: rgba(107, 101, 132, 0.6);
   }
`;

const IssueRow = styled.button`
   display: flex;
   align-items: stretch;
   gap: 0;
   width: 100%;
   flex: none;
   flex-shrink: 0;
   text-align: left;
   border: 1px solid rgba(230, 226, 246, 0.95);
   background: rgba(255, 255, 255, 0.88);
   border-radius: 14px;
   padding: 0;
   margin: 0 0 8px;
   cursor: pointer;
   overflow: hidden;
   transition:
      background 0.15s ease,
      border-color 0.15s ease,
      box-shadow 0.15s ease;

   &:last-child {
      margin-bottom: 0;
   }

   &:hover {
      background: #fff;
      border-color: ${theme.colors.primary}55;
      box-shadow: 0 8px 22px -14px rgba(29, 20, 70, 0.28);

      .go {
         opacity: 1;
         color: ${theme.colors.primary};
      }
   }

   .rail {
      width: 4px;
      flex-shrink: 0;
      background: #94a3b8;
   }

   &.error .rail {
      background: linear-gradient(180deg, #f87171, #b91c1c);
   }

   &.warning .rail {
      background: linear-gradient(180deg, #fdba74, #c2410c);
   }

   &.error .badge {
      background: #fee2e2;
      color: #b91c1c;
   }

   &.warning .badge {
      background: #ffedd5;
      color: #c2410c;
   }

   .body {
      min-width: 0;
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 10px 10px 10px 12px;
   }

   .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 8px;
   }

   .title {
      font-size: 13.5px;
      font-weight: 700;
      color: ${theme.colors.text};
      letter-spacing: -0.01em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
   }

   .badge {
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      padding: 3px 7px;
      border-radius: 999px;
      flex-shrink: 0;
      margin-top: 1px;
   }

   .meta {
      font-size: 11px;
      color: ${theme.colors.textTertiary};
      font-family: 'IBM Plex Mono', ui-monospace, monospace;
   }

   .msg {
      display: block;
      margin-top: 4px;
      font-size: 12.5px;
      line-height: 1.4;
      color: ${theme.colors.textSecondary};
   }

   .hint {
      display: block;
      margin-top: 4px;
      font-size: 11.5px;
      line-height: 1.4;
      color: ${theme.colors.textTertiary};
   }

   .go {
      display: flex;
      align-items: flex-start;
      padding: 12px 12px 0 0;
      flex-shrink: 0;
      opacity: 0.35;
      color: ${theme.colors.textSecondary};
      transition: opacity 0.15s ease, color 0.15s ease;
   }
`;

const EmptyState = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 8px;
   min-height: 140px;
   padding: 1.25rem;
   text-align: center;
   color: ${theme.colors.textSecondary};

   svg {
      color: ${theme.colors.green};
   }

   p {
      margin: 0;
      font-size: 13px;
      line-height: 1.5;
      max-width: 280px;
   }

   button {
      border: 0;
      background: transparent;
      color: ${theme.colors.primary};
      font-size: 12.5px;
      font-weight: 700;
      cursor: pointer;
      padding: 4px 6px;
      border-radius: 6px;

      &:hover {
         background: rgba(79, 63, 224, 0.08);
      }
   }
`;

const PanelFoot = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   padding: 12px 14px;
   border-top: 1px solid rgba(230, 226, 246, 0.9);
   background: rgba(255, 255, 255, 0.55);
   flex-shrink: 0;

   button {
      border: 0;
      border-radius: 10px;
      padding: 9px 14px;
      font-size: 12.5px;
      font-weight: 700;
      cursor: pointer;
      transition: background 0.15s ease, transform 0.15s ease;
   }

   .ghost {
      background: ${theme.surface.hoverFill};
      color: ${theme.colors.textSecondary};

      &:hover {
         background: ${theme.colors.border};
         color: ${theme.colors.text};
      }
   }

   .primary {
      background: ${theme.gradients.primary};
      color: #fff;
      box-shadow: 0 8px 18px -10px rgba(79, 63, 224, 0.65);

      &:hover {
         transform: translateY(-1px);
      }
   }
`;
